import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { LogIn, UserCircle } from 'lucide-react';
import { isAppInstalled } from '@/utils/registerServiceWorker';

/**
 * PWA Login Shortcut - Shows a floating login button for easy access
 * Only visible when:
 * - App is installed as PWA
 * - User is not logged in
 * - Not already on auth pages
 */
export function PWALoginShortcut() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPWA, setIsPWA] = useState(false);
  const [showShortcut, setShowShortcut] = useState(false);

  useEffect(() => {
    setIsPWA(isAppInstalled());
  }, []);

  useEffect(() => {
    // In development or PWA mode, show the shortcut
    const isDev = import.meta.env.DEV;
    
    // Show shortcut if:
    // 1. In development OR running as PWA
    // 2. User not logged in
    // 3. Not on auth pages
    const isAuthPage = location.pathname.startsWith('/auth') || 
                       location.pathname.startsWith('/login') ||
                       location.pathname.startsWith('/signup');
    
    setShowShortcut((isDev || isPWA) && !user && !isAuthPage);
  }, [isPWA, user, location.pathname]);

  if (!showShortcut) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2">
      <Button
        onClick={() => navigate('/auth')}
        className="h-12 w-12 rounded-full p-0 shadow-lg bg-white hover:bg-gray-50 border border-gray-200"
        title="Staff Login"
      >
        <LogIn className="h-6 w-6 text-orange-600" />
      </Button>
      {isPWA && (
        <div className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
          PWA
        </div>
      )}
    </div>
  );
}

/**
 * Profile Menu Shortcut - Quick access to profile and logout
 * Shows when user is logged in
 */
export function PWAProfileShortcut() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    setIsPWA(isAppInstalled());
  }, []);

  if (!isPWA || !user) return null;

  return (
    <div className="fixed top-4 left-4 z-40">
      <Button
        variant="outline"
        size="icon"
        onClick={() => navigate('/admin/profile')}
        className="h-12 w-12 rounded-full shadow-lg bg-white hover:bg-gray-50"
        title="Profile"
      >
        <UserCircle className="h-6 w-6 text-orange-600" />
      </Button>
    </div>
  );
}
